import { useEffect } from 'react';
import {
  Archive,
  BookOpen,
  Boxes,
  CalendarDays,
  Camera,
  ChevronRight,
  Leaf,
  Shirt,
  Sparkles,
  Warehouse,
  X,
} from 'lucide-react';
import { MirrorReflector } from './branding.jsx';

const ACTION_GROUPS = [
  {
    label: 'Capture',
    actions: [
      { id: 'capture', label: 'Capture item', detail: 'Camera or photo library', icon: Camera },
      { id: 'room', label: 'Add to a room', detail: 'Record something around you', icon: Boxes },
    ],
  },
  {
    label: 'Surroundings',
    actions: [
      { id: 'garden', label: 'Log plant', detail: 'Water, feed, or leave a note', icon: Leaf, tone: 'care' },
      { id: 'library', label: 'Add to library', detail: 'Book, film, music, or media', icon: BookOpen },
      { id: 'storage', label: 'Add to storage', detail: 'Box, bin, shelf, or location', icon: Warehouse },
    ],
  },
  {
    label: 'Plan',
    actions: [
      { id: 'schedule', label: 'Add to schedule', detail: 'Event, reminder, or outfit plan', icon: CalendarDays, tone: 'reminder' },
      { id: 'looks', label: 'Build a look', detail: 'Try-on, OoTD, and saved fits', icon: Shirt },
      { id: 'intake', label: 'Organize intake', detail: 'Place recent captures', icon: Archive },
      { id: 'witness', label: 'Ask Mirror', detail: 'Write a note and reflect', icon: Sparkles },
    ],
  },
];

export default function QuickActions({ open, onClose, onCapture, onNavigate, onSurroundingCapture }) {
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = event => {
      if (event.key === 'Escape') onClose?.();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, open]);

  if (!open) return null;

  const run = id => {
    onClose?.();
    if (id === 'capture') return onCapture?.();
    if (['room', 'garden', 'library', 'storage'].includes(id)) {
      const branch = id === 'room' ? 'all' : id;
      return onSurroundingCapture?.(branch);
    }
    if (id === 'witness') {
      onNavigate?.('today');
      window.setTimeout(() => {
        const details = document.getElementById('witness-title')?.closest('details');
        if (details) details.open = true;
        details?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
      return;
    }
    onNavigate?.(id);
  };

  return <div className="quick-action-backdrop" role="presentation" onMouseDown={event => event.target === event.currentTarget && onClose?.()}>
    <section className="quick-action-shelf" role="dialog" aria-modal="true" aria-labelledby="quick-action-title">
      <header>
        <div><MirrorReflector /><span><small>Mirror action shelf</small><strong id="quick-action-title">What do you want to reflect?</strong></span></div>
        <button type="button" onClick={onClose} aria-label="Close quick actions"><X /></button>
      </header>
      <div className="quick-action-groups">
        {ACTION_GROUPS.map(group => <section key={group.label}>
          <h2>{group.label}</h2>
          <div>{group.actions.map(action => { const Icon = action.icon; return <button type="button" className={action.tone ? `tone-${action.tone}` : ''} onClick={() => run(action.id)} key={action.id}><span className="quick-action-icon"><Icon /></span><span><strong>{action.label}</strong><small>{action.detail}</small></span><ChevronRight /></button>; })}</div>
        </section>)}
      </div>
      <footer><span>Signed-in changes sync privately. Party sharing is always explicit.</span><button type="button" onClick={() => run('capture')}><Camera /> Capture now</button></footer>
    </section>
  </div>;
}
